import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isJournalStep, type JournalStep } from '../steps';
import type { JournalEntry, JournalWarmupState } from '../../../../types/domain/journal';
import type { ChatMessage } from '../../../../types/domain/chat';

export type JournalFlowLogger = {
  info?: (event: string, payload?: Record<string, unknown>) => void;
  warn?: (event: string, payload?: Record<string, unknown>) => void;
  error?: (event: string, error: unknown, payload?: Record<string, unknown>) => void;
};

export type JournalFlowWarmupPayload = {
  moodLevel?: number | null;
  energyLevel?: number | null;
};

export type JournalFlowWritingPayload = {
  content: string;
  commitmentText: string;
  alertTherapist: boolean;
};

export type JournalFlowApiAdapter = {
  advanceStep: (entryId: number, step?: JournalStep) => Promise<JournalEntry>;
  updateWarmup: (entryId: number, payload: JournalFlowWarmupPayload) => Promise<JournalEntry>;
  toggleCard: (entryId: number, cardId: number) => Promise<JournalEntry>;
  shuffleHand: (entryId: number) => Promise<JournalEntry>;
  saveWriting: (entryId: number, payload: JournalFlowWritingPayload) => Promise<JournalEntry>;
};

export type JournalFlowConfig = {
  entry: JournalEntry;
  api: JournalFlowApiAdapter;
  logger?: JournalFlowLogger;
  autosaveDelayMs?: number;
  maxSelectableCards?: number;
  onInvalidStep?: (value: unknown, entry: JournalEntry) => void;
};

export type FlowActionAvance = (step?: JournalStep) => Promise<boolean>;

export type JournalFlowHookReturn = {
  entry: JournalEntry;
  warmupDraft: JournalWarmupState;
  writingDraft: string;
  commitmentDraft: string;
  alertTherapistDraft: boolean;
  step: JournalStep;
  terminated: boolean;
  advancing: boolean;
  actions: {
    advance: FlowActionAvance;
    setWarmupDraft: (values: Partial<JournalWarmupState & { summary?: string | null }>) => void;
    updateWarmup: (payload: JournalFlowWarmupPayload) => Promise<void>;
    setWritingDraft: (value: string) => void;
    setCommitmentDraft: (value: string) => void;
    setAlertTherapistDraft: (value: boolean) => void;
    toggleCardSelection: (cardId: number) => Promise<void>;
    shuffleHand: () => Promise<void>;
    saveWriting: (payload: JournalFlowWritingPayload) => Promise<void>;
    setCoachTranscript: (messages: ChatMessage[]) => void;
  };
};

const DEFAULT_AUTOSAVE_DELAY_MS = 5000;

function extractDraft(entry: JournalEntry) {
  return entry.writing.finalText || '';
}

const noopLogger: Required<JournalFlowLogger> = {
  info: () => {},
  warn: () => {},
  error: () => {},
};

export function useJournalFlow({
  entry: initialEntry,
  api,
  logger,
  autosaveDelayMs = DEFAULT_AUTOSAVE_DELAY_MS,
  onInvalidStep,
}: JournalFlowConfig): JournalFlowHookReturn {
  const activeLogger = useMemo(
    () => ({
      ...noopLogger,
      ...logger,
    }),
    [logger],
  );

  const [entry, setEntry] = useState<JournalEntry>(initialEntry);
  const [warmupDraft, setWarmupDraftState] = useState(initialEntry.warmup);
  const warmupDirtyRef = useRef(false);

  const [writingDraft, setWritingDraftState] = useState<string>(() => extractDraft(entry));
  const draftDirtyRef = useRef(false);

  useEffect(() => {
    setWritingDraft(extractDraft(entry));
  }, [entry.writing]);

  const [commitmentDraft, setCommitmentDraftState] = useState<string>(
    () => entry.writing.commitmentText ?? '',
  );
  const commitmentDirtyRef = useRef(false);

  const [alertTherapistDraft, setAlertTherapistDraftState] = useState<boolean>(() =>
    Boolean(entry.writing.alertTherapist),
  );
  const alertTherapistDirtyRef = useRef(false);

  const [, setWritingStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [isAdvancing, setIsAdvancing] = useState(false);

  const autosaveRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warmupMutationRef = useRef(0);

  const [isTerminated, setIsTerminated] = useState(false);

  const currentStepValue = entry.currentStep;
  const isCurrentStepValid = useMemo(() => isJournalStep(currentStepValue), [currentStepValue]);

  useEffect(() => {
    if (!isCurrentStepValid) {
      setIsTerminated(true);
      onInvalidStep?.(currentStepValue, entry);
      activeLogger.error?.('journal_flow.invalid_step', new Error('Invalid journal step'), {
        entry,
        currentStep: currentStepValue ?? null,
      });
    }
  }, [activeLogger, entry, isCurrentStepValid, currentStepValue, onInvalidStep]);

  const currentStep = currentStepValue;

  const applyEntryUpdate = useCallback(
    (
      nextEntry: JournalEntry,
      options: {
        syncWarmup?: boolean;
        forceWarmup?: boolean;
        syncDraft?: boolean;
        forceDraft?: boolean;
        syncCommitment?: boolean;
        forceCommitment?: boolean;
        syncAlertTherapist?: boolean;
        forceAlertTherapist?: boolean;
      } = {},
    ) => {
      setEntry(() => ({ ...nextEntry }));

      if (options.syncWarmup) {
        const nextWarmup = nextEntry.warmup;
        if (options.forceWarmup || !warmupDirtyRef.current) {
          setWarmupDraftState(nextWarmup);
          if (options.forceWarmup) {
            warmupDirtyRef.current = false;
          }
        }
      }

      if (options.syncDraft) {
        const nextDraft = extractDraft(nextEntry);
        if (options.forceDraft || !draftDirtyRef.current) {
          setWritingDraftState(nextDraft);
          if (options.forceDraft) {
            draftDirtyRef.current = false;
          }
        }
      }

      if (options.syncCommitment) {
        const nextCommitment = nextEntry.writing.commitmentText ?? '';
        if (options.forceCommitment || !commitmentDirtyRef.current) {
          setCommitmentDraftState(nextCommitment);
          if (options.forceCommitment) {
            commitmentDirtyRef.current = false;
          }
        }
      }

      if (options.syncAlertTherapist) {
        const nextAlertTherapist = Boolean(nextEntry.writing.alertTherapist);
        if (options.forceAlertTherapist || !alertTherapistDirtyRef.current) {
          setAlertTherapistDraftState(nextAlertTherapist);
          if (options.forceAlertTherapist) {
            alertTherapistDirtyRef.current = false;
          }
        }
      }
    },
    [],
  );

  const setWritingDraft = useCallback((value: string) => {
    draftDirtyRef.current = true;
    setWritingDraftState((prev) => (prev === value ? prev : value));
  }, []);

  const setCommitmentDraft = useCallback((value: string) => {
    commitmentDirtyRef.current = true;
    setCommitmentDraftState((prev) => (prev === value ? prev : value));
  }, []);

  const setAlertTherapistDraft = useCallback((value: boolean) => {
    alertTherapistDirtyRef.current = true;
    setAlertTherapistDraftState((prev) => (prev === value ? prev : value));
  }, []);

  const advance = useCallback(
    async (overrideStep?: JournalStep) => {
      setIsAdvancing(true);
      const entryId = entry.id;

      try {
        const updatedEntry = await api.advanceStep(entryId, overrideStep);
        applyEntryUpdate(updatedEntry, { syncWarmup: true });

        activeLogger.info?.('journal_flow.step_advanced', {
          entryId,
          targetStep: overrideStep ?? null,
        });

        setIsAdvancing(false);
        return true;
      } catch (error) {
        activeLogger.error?.('journal_flow.advance_failed', error);
        setIsAdvancing(false);
        return false;
      }
    },
    [activeLogger, api, applyEntryUpdate, entry.id],
  );

  const warmupConfirmed = entry.warmup;

  const updateWarmup = useCallback(
    async ({ moodLevel, energyLevel }: JournalFlowWarmupPayload) => {
      const entryId = entry.id;
      if (!entryId) {
        activeLogger.warn?.('journal_flow.warmup_skipped', {
          reason: 'entry_missing_id',
        });
        return;
      }

      const mutationId = warmupMutationRef.current + 1;
      warmupMutationRef.current = mutationId;

      try {
        const response = await api.updateWarmup(entryId, { moodLevel, energyLevel });
        const updatedEntry = response;
        const isLatestMutation = mutationId === warmupMutationRef.current;

        if (isLatestMutation) {
          warmupDirtyRef.current = false;
          applyEntryUpdate(updatedEntry, { syncWarmup: true, forceWarmup: true });
        } else {
          applyEntryUpdate(updatedEntry);
        }

        activeLogger.info?.('journal_flow.warmup_saved', {
          entryId,
          moodLevel,
          energyLevel,
        });
      } catch (error) {
        if (mutationId === warmupMutationRef.current) {
          warmupDirtyRef.current = false;
          setWarmupDraftState(warmupConfirmed);
        }
        throw error;
      }
    },
    [activeLogger, api, applyEntryUpdate, entry.id, warmupConfirmed],
  );

  const setWarmupDraft = useCallback(
    (values: Partial<JournalWarmupState & { summary?: string | null }>) => {
      warmupDirtyRef.current = true;
      setWarmupDraftState((prev) => ({
        ...prev,
        ...values,
      }));
    },
    [],
  );

  const toggleCard = useCallback(
    async (cardId: number) => {
      const entryId = entry.id;
      const updatedEntry = await api.toggleCard(entryId, cardId);

      applyEntryUpdate(updatedEntry, { syncWarmup: true });

      setEntry((prev) => ({
        ...prev,
        hand: updatedEntry.hand,
      }));
    },
    [activeLogger, api, applyEntryUpdate, entry.id, setEntry],
  );

  const shuffleHand = useCallback(async () => {
    const entryId = entry.id;

    try {
      const updatedEntry = await api.shuffleHand(entryId);
      applyEntryUpdate(updatedEntry, { syncWarmup: true });

      setEntry((prev) => ({
        ...prev,
        hand: updatedEntry.hand,
      }));

      activeLogger.info?.('journal_flow.hand_shuffled', { entryId });
    } catch (error) {
      activeLogger.error?.('journal_flow.hand_shuffle_failed', error);
      throw error;
    }
  }, [activeLogger, api, applyEntryUpdate, entry.id, setEntry]);

  const setCoachTranscript = useCallback((messages: ChatMessage[]) => {
    setEntry((prev) => ({
      ...prev,
      chat: prev.chat ? { ...prev.chat, messages } : { messages },
    }));
  }, []);

  const saveWriting = useCallback(
    async (payload: JournalFlowWritingPayload) => {
      const entryId = entry.id;
      if (autosaveRef.current) {
        clearTimeout(autosaveRef.current);
        autosaveRef.current = null;
      }

      setWritingStatus('saving');
      try {
        const updatedEntry = await api.saveWriting(entryId, payload);
        applyEntryUpdate(updatedEntry, {
          syncWarmup: true,
          forceWarmup: true,
          syncDraft: true,
          forceDraft: true,
          syncCommitment: true,
          forceCommitment: true,
          syncAlertTherapist: true,
          forceAlertTherapist: true,
        });
        setWritingStatus('saved');

        activeLogger.info?.('journal_flow.writing_saved', {
          entryId,
          writing: updatedEntry.writing,
        });

        autosaveRef.current = setTimeout(() => {
          setWritingStatus('idle');
        }, autosaveDelayMs);

        setEntry((prev) => ({
          ...prev,
          writing: updatedEntry.writing,
        }));
      } catch (error) {
        setWritingStatus('idle');
        activeLogger.error?.('journal_flow.writing_failed', error);
        throw error;
      }
    },
    [activeLogger, api, applyEntryUpdate, autosaveDelayMs, entry.id],
  );

  return {
    entry,
    warmupDraft,
    writingDraft,
    commitmentDraft,
    alertTherapistDraft,
    step: currentStep as string,
    terminated: isTerminated,
    advancing: isAdvancing,
    actions: {
      advance,
      setWarmupDraft,
      updateWarmup,
      setWritingDraft,
      setCommitmentDraft,
      setAlertTherapistDraft,
      toggleCardSelection: toggleCard,
      shuffleHand,
      saveWriting,
      setCoachTranscript,
    },
  };
}
